// src/components/Devices.js
import React, { useState, useEffect, useRef } from "react";
import { db, storage, auth } from "../firebase";
import { useFreeLimits } from "../hooks/useFreeLimits";
import UpgradeModal from "./UpgradeModal";
import {
  collection,
  addDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
} from "firebase/firestore";
import {
  ref,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { reauthenticateWithCredential, EmailAuthProvider } from "firebase/auth";
import { FiEye } from "react-icons/fi";
import { FaYoutube } from "react-icons/fa";
import PageScaffold from "./ui/PageScaffold";
import VideoLightbox from "./ui/VideoLightbox";
import { useAccess } from "../access/AccessContext";
import "../styles/layout.css";

const PHONE_MAKES = [
  "Apple",
  "Samsung",
  "Google",
  "Huawei",
  "Xiaomi",
  "OnePlus",
  "Sony",
  "Nokia",
  "Motorola",
  "Oppo",
  "Vivo",
  "LG",
  "Realme",
  "Asus",
  "Honor",
  "Other",
];
const COMPUTER_MAKES = [
  "Apple",
  "Dell",
  "HP",
  "Lenovo",
  "Asus",
  "Acer",
  "Microsoft",
  "Razer",
  "MSI",
  "Samsung",
  "Toshiba",
  "Huawei",
  "LG",
  "Sony",
  "Other",
];

function defaultForm(type = "phone", uid = "") {
  return {
    uid,
    type,
    nickname: "",
    make: "",
    imageUrl: "",
    imageName: "",
    phoneNumber: "",
    phonePasscode: "",
    username: "",
    loginPassword: "",
    contact: "",
    notes: "",
  };
}

// helpers
const isHttp = (u) => typeof u === "string" && /^https?:\/\//i.test(u);
function toStoragePath(maybe) {
  if (!maybe || typeof maybe !== "string") return null;
  if (maybe.startsWith("gs://")) {
    const no = maybe.replace(/^gs:\/\//, "");
    const slash = no.indexOf("/");
    return slash >= 0 ? no.slice(slash + 1) : null;
  }
  if (maybe.startsWith("http")) {
    const m = maybe.match(/\/o\/([^?]+)/);
    if (!m) return null;
    try {
      return decodeURIComponent(m[1]);
    } catch {
      return m[1];
    }
  }
  if (maybe.includes("..")) return null;
  return maybe;
}

function downloadTextFile(name, content) {
  const blob = new Blob([content || ""], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name || "details.txt";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

export default function Devices({ user }) {
  const { readOnly, ownerUid: managedOwnerUid } =
    useAccess() || { readOnly: false, ownerUid: null };

  // ✅ acting uid (owner when in manager-view; otherwise self)
  const actingUid = managedOwnerUid || user?.uid;

  const [devices, setDevices] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const { free } = useFreeLimits(user);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [formType, setFormType] = useState("phone");
  const [form, setForm] = useState(defaultForm("phone", actingUid));
  const [uploading, setUploading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [confirm, setConfirm] = useState("");
  const [showPasscode, setShowPasscode] = useState(false);
  const [showLoginPwd, setShowLoginPwd] = useState(false);
  const fileInput = useRef();

  const [showVideo, setShowVideo] = useState(false);
  const [showDrawer, setShowDrawer] = useState(false);

  const [viewOpen, setViewOpen] = useState(false);
  const [viewItem, setViewItem] = useState(null);

  const [viewPwdRevealed, setViewPwdRevealed] = useState(false);

  // subscribe to the correct owner scope
  useEffect(() => {
    if (!actingUid) return;
    const qy = query(
      collection(db, "devices"),
      where("uid", "==", actingUid),
      orderBy("created", "desc")
    );
    const unsub = onSnapshot(qy, (snap) =>
      setDevices(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );
    return () => unsub();
  }, [actingUid]);

  function openForm(device = null) {
    if (readOnly) return;
    if (!device && free && devices.length >= 1) { setShowUpgrade(true); return; }
    setShowForm(true);
    setConfirm("");
    setShowPasscode(false);
    setShowLoginPwd(false);


    if (device) {
      setForm({ ...device });
      setFormType(device.type || "phone");
      setEditingId(device.id);
    } else {
      setForm(defaultForm(formType, actingUid));
      setEditingId(null);
    }
    if (fileInput.current) fileInput.current.value = "";
  }
  function closeForm() {
    setShowForm(false);
    setForm(defaultForm(formType, actingUid));
    setEditingId(null);
    setConfirm("");
    setShowPasscode(false);
    setShowLoginPwd(false);

  }
  function handleInput(e) {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  }

  const passValid =
    formType === "phone"
      ? form.phonePasscode === confirm
      : form.loginPassword === confirm;

  async function handleImage(e) {
    if (readOnly) return;
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const imageName = `${actingUid}_${Date.now()}_${file.name}`;
      const storageRef = ref(storage, `devices/${actingUid}/${imageName}`);
      const uploadTask = uploadBytesResumable(storageRef, file);
      await new Promise((res, rej) =>
        uploadTask.on("state_changed", null, rej, res)
      );
      const imageUrl = await getDownloadURL(uploadTask.snapshot.ref);
      setForm((f) => ({ ...f, imageUrl, imageName }));
      if (fileInput.current) fileInput.current.value = "";
    } finally {
      setUploading(false);
    }
  }

  async function removeImage() {
    if (readOnly) return;
    if (form.imageUrl) {
      try {
        let storagePath = form.imageName
          ? `devices/${actingUid}/${form.imageName}`
          : decodeURIComponent(form.imageUrl.split("/o/")[1]?.split("?")[0] || "");
        if (storagePath) {
          await deleteObject(ref(storage, storagePath)).catch(() => {});
        }
      } catch {}
    }
    setForm((f) => ({ ...f, imageUrl: "", imageName: "" }));
    if (fileInput.current) fileInput.current.value = "";
  }

  async function promptAndReauthenticate(message) {
    if (!auth?.currentUser) {
      alert("You are not signed in. Please log in and try again.");
      return false;
    }

    const emailAddr = auth.currentUser.email;
    if (!emailAddr) {
      alert("Your account must use an email/password login to unlock this value.");
      return false;
    }

    const pwd = window.prompt(message);
    if (!pwd) return false;

    try {
      const credential = EmailAuthProvider.credential(emailAddr, pwd);
      await reauthenticateWithCredential(auth.currentUser, credential);
      return true;
    } catch (err) {
      const msg = err?.message || "Incorrect password. Please try again.";
      alert(msg);
      return false;
    }
  }

  async function handleReveal(type) {
    const setter = type === "passcode" ? setShowPasscode : setShowLoginPwd;
    const isShowing = type === "passcode" ? showPasscode : showLoginPwd;
    if (isShowing) {
      setter(false);
      return;
    }

    const ok = await promptAndReauthenticate(
      "Enter your account password to reveal this value:"
    );
    if (ok) setter(true);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (readOnly) return;

    if (!auth?.currentUser?.uid) {
      alert("You are not signed in. Please log in and try again.");
      return;
    }

    setUploading(true);
    try {
      const base = {
        ...form,
        type: formType,
        uid: actingUid,
        updated: serverTimestamp(),
      };

      if (editingId) {
        await updateDoc(doc(db, "devices", editingId), base);
      } else {
        await addDoc(collection(db, "devices"), {
          ...base,
          created: serverTimestamp(),
        });
      }

      closeForm();
    } catch (err) {
      console.error(
        "[DEVICES save] error.code =",
        err?.code,
        "message =",
        err?.message,
        "raw =",
        err
      );
      alert("Error saving device: " + (err?.message || String(err)));
    } finally {
      setUploading(false);
    }
  }

  async function handleDelete() {
    if (readOnly) return;
    if (!editingId) return;
    if (!window.confirm("Delete this device?")) return;
    if (form.imageUrl) {
      try {
        const storagePath = form.imageName
          ? `devices/${actingUid}/${form.imageName}`
          : decodeURIComponent(form.imageUrl.split("/o/")[1]?.split("?")[0] || "");
        if (storagePath) await deleteObject(ref(storage, storagePath));
      } catch {}
    }
    await deleteDoc(doc(db, "devices", editingId));
    closeForm();
  }

  function prettyType(t) {
    if (!t) return "Device";
    return t.charAt(0).toUpperCase() + t.slice(1);
  }

  function renderForm() {
    const isPhone = formType === "phone";
    return (
      <div className="modal-bg" onClick={closeForm}>
        <form
          className="modal-card"
          onClick={(e) => e.stopPropagation()}
          onSubmit={handleSubmit}
        >
          <h3>{editingId ? "Edit Device" : "Add Device"}</h3>

          <div className="modal-body">
            <div className="dv-type-row">
              <div className="btn-group dv-type-buttons">
                {["phone", "computer", "other"].map((t) => (
                  <button
                    type="button"
                    key={t}
                    className={`btn ${formType === t ? "btn-main" : ""}`}
                    onClick={() => {
                      setFormType(t);
                      setForm(defaultForm(t, actingUid));
                      setConfirm("");
                      setShowPasscode(false);
                      setShowLoginPwd(false);
                      
                      if (fileInput.current) fileInput.current.value = "";
                    }}
                  >
                    {t === "phone"
                      ? "Phone"
                      : t === "computer"
                      ? "Computer"
                      : "Other Device"}
                  </button>
                ))}
              </div>
              <div className="dv-type-select">
                <label className="label" htmlFor="dvTypeSel">
                  Device Type
                </label>
                <div className="select-modern">
                  <select
                    id="dvTypeSel"
                    value={formType}
                    onChange={(e) => {
                      const t = e.target.value;
                      setFormType(t);
                      setForm(defaultForm(t, actingUid));
                      setConfirm("");
                      setShowPasscode(false);
                      setShowLoginPwd(false);
                     
                      if (fileInput.current) fileInput.current.value = "";
                    }}
                  >
                    <option value="phone">Phone</option>
                    <option value="computer">Computer</option>
                    <option value="other">Other Device</option>
                  </select>
                  <span className="caret" aria-hidden>
                    ▾
                  </span>
                </div>
              </div>
            </div>

            <label className="label">Device Nickname</label>
            <input
              name="nickname"
              type="text"
              className="input"
              placeholder="Device Nickname"
              value={form.nickname}
              onChange={handleInput}
              required
            />

            <label className="label">Make</label>
            <select
              name="make"
              className="input"
              value={form.make}
              onChange={handleInput}
              required
            >
              <option value="">Select Make</option>
              {(isPhone ? PHONE_MAKES : COMPUTER_MAKES).map((make) => (
                <option key={make} value={make}>
                  {make}
                </option>
              ))}
            </select>

            {isPhone ? (
              <>
                <label className="label">Phone Number</label>
                <input
                  name="phoneNumber"
                  type="text"
                  className="input"
                  placeholder="Phone Number"
                  value={form.phoneNumber}
                  onChange={handleInput}
                />

                <label className="label">Passcode</label>
                <div className="input-wrap-reveal">
                  <input
                    name="phonePasscode"
                    type={showPasscode ? "text" : "password"}
                    className="input"
                    placeholder="Passcode"
                    value={form.phonePasscode}
                    onChange={handleInput}
                  />
                  <button
                    type="button"
                    className="reveal-btn"
                    onClick={() => handleReveal("passcode")}
                  >
                    {showPasscode ? "Hide" : "Reveal"}
                  </button>
                </div>

                <label className="label">Confirm Passcode</label>
                <input
                  type="password"
                  className="input"
                  placeholder="Confirm Passcode"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                />
                {confirm && !passValid && (
                  <div style={{ color: "#b40000", marginTop: -4, marginBottom: 8 }}>
                    Passcodes do not match.
                  </div>
                )}
              </>
            ) : (
              <>
                <label className="label">Username</label>
                <input
                  name="username"
                  type="text"
                  className="input"
                  placeholder="Username"
                  value={form.username}
                  onChange={handleInput}
                />

                <label className="label">Password</label>
                <div className="input-wrap-reveal">
                  <input
                    name="loginPassword"
                    type={showLoginPwd ? "text" : "password"}
                    className="input"
                    placeholder="Password"
                    value={form.loginPassword}
                    onChange={handleInput}
                  />
                  <button
                    type="button"
                    className="reveal-btn"
                    onClick={() => handleReveal("login")}
                  >
                    {showLoginPwd ? "Hide" : "Reveal"}
                  </button>
                </div>

                <label className="label">Confirm Password</label>
                <input
                  type="password"
                  className="input"
                  placeholder="Confirm Password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                />
                {confirm && !passValid && (
                  <div style={{ color: "#b40000", marginTop: -4, marginBottom: 8 }}>
                    Passwords do not match.
                  </div>
                )}
              </>
            )}

            <label className="label">Notes (optional)</label>
            <textarea
              name="notes"
              className="input"
              placeholder="Notes (optional)"
              rows={3}
              value={form.notes}
              onChange={handleInput}
            />

            <label className="label">Device Image</label>
            {form.imageUrl ? (
              <div className="dv-img-thumb">
                <img src={form.imageUrl} alt="Device" />
                <button
                  type="button"
                  className="dv-img-remove"
                  onClick={removeImage}
                  title="Remove image"
                >
                  ×
                </button>
              </div>
            ) : (
              <input
                ref={fileInput}
                type="file"
                accept="image/*"
                className="input"
                onChange={handleImage}
                disabled={uploading}
              />
            )}

             <div className="dv-act-wrap">

              <div className="dv-act-panel">
                <button
                  type="submit"
                  className="btn btn-main"
                  disabled={uploading || (confirm && !passValid)}
                >
                  {editingId ? "Update" : "Add"}
                </button>
                {editingId && (
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={handleDelete}
                    disabled={uploading}
                  >
                    Delete
                  </button>
                )}
                <button
                  type="button"
                  className="btn btn-cancel"
                  onClick={closeForm}
                  disabled={uploading}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </form>

        <style>{`
          .dv-type-row { display: grid; grid-template-columns: 1fr; gap: 10px; margin-bottom: 8px; }
          .dv-type-buttons { display: none; }
          .dv-type-select { display: block; }
          @media (min-width: 700px) { .dv-type-buttons { display: flex; } .dv-type-select { display: none; } }

          .select-modern { position: relative; display: inline-block; width: 100%; }
          .select-modern select { appearance: none; width: 100%; padding: 10px 40px 10px 12px; border: 1px solid #d8dce5; border-radius: 10px; background: #fff; font-size: 1rem; }
          .select-modern .caret { position: absolute; right: 12px; top: 50%; transform: translateY(-50%); pointer-events: none; color: #657899; font-size: 14px; }

          .input-wrap-reveal { position: relative; }
          .reveal-btn { position: absolute; right: 8px; top: 50%; transform: translateY(-50%); border: none; background: #f3f4f6; padding: 6px 10px; border-radius: 6px; cursor: pointer; }

          .dv-img-thumb { position: relative; width: 120px; height: 120px; border-radius: 10px; overflow: hidden; box-shadow: 0 2px 10px rgba(0,0,0,.08); margin-bottom: 10px; }
          .dv-img-thumb img { width: 100%; height: 100%; object-fit: cover; display: block; }
          .dv-img-remove { position: absolute; top: 6px; right: 6px; width: 28px; height: 28px; border-radius: 50%; border: none; background: rgba(0,0,0,.6); color: #fff; font-size: 18px; line-height: 28px; text-align: center; cursor: pointer; }

          .dv-act-wrap { margin-top: 12px; }
         .dv-act-panel { display: flex; flex-direction: column; gap: 8px; margin-top: 10px; }
          .dv-act-panel .btn { width: 100%; }
          @media (min-width: 700px) {
            .dv-act-panel { flex-direction: row; align-items: center; }
            .dv-act-panel .btn { width: auto; min-width: 140px; }
          }
        `}</style>
      </div>
    );
  }

  async function getSignedUrlFor(pathOrUrl) {
    const ownerUid = managedOwnerUid || user?.uid;
    const p = isHttp(pathOrUrl) ? toStoragePath(pathOrUrl) : pathOrUrl;
    if (!p || !ownerUid) return null;
    try {
      const idToken = await auth.currentUser.getIdToken();
      const resp = await fetch("/api/accountManagers/getDownloadUrl", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({ ownerUid, storagePath: p }),
      });
      if (!resp.ok) return null;
      const data = await resp.json();
      return data?.url || null;
    } catch {
      return null;
    }
  }

  function DeviceCard({ device }) {
    const onPrimary = readOnly ? () => openView(device) : () => openForm(device);
    const label = readOnly ? "View" : "Edit";
    return (
      <div className="card dv-item">
        <div className="dv-left">
          {device.imageUrl ? (
            <img src={device.imageUrl} alt="" className="tile-thumb" />
          ) : (
            <div
              className="tile-thumb"
              style={{ display: "grid", placeItems: "center", background: "#f3f3f3" }}
            >
              📱
            </div>
          )}
        </div>
        <div className="dv-main">
          <div className="tile-title">{device.nickname || "(Untitled)"}</div>
          <div className="tile-sub">
            {prettyType(device.type)}
            {device.make ? ` · ${device.make}` : ""}
          </div>
          {device.phoneNumber && (
            <div className="tile-sub">Number: {device.phoneNumber}</div>
          )}
          {device.username && (
            <div className="tile-sub">User: {device.username}</div>
          )}
        </div>
        <div className="dv-actions">
          <button
            className="btn btn-main btn-edit"
            onClick={onPrimary}
            {...(readOnly ? { "data-allow-readonly": "1" } : {})}
          >
            {label}
          </button>
        </div>
      </div>
    );
  }

  function openView(item) {
    setViewItem(item);
    setViewOpen(true);
    setViewPwdRevealed(false);
  }
  function closeView() {
    setViewOpen(false);
    setViewItem(null);
  }

  async function handleRevealRequest() {
    if (viewPwdRevealed) return;

    const ok = await promptAndReauthenticate(
      "Enter your account password to unlock this value:"
    );
    if (ok) setViewPwdRevealed(true);
  }

  const viewingIsPhone = (viewItem?.type || "phone") === "phone";
  const viewingSecretValue = viewItem
    ? viewingIsPhone
      ? viewItem.phonePasscode
      : viewItem.loginPassword
    : null;
  const viewingSecretLabel = viewingIsPhone ? "Passcode" : "Password";
  const viewingSecretDisplay = viewingSecretValue
    ? viewPwdRevealed
      ? viewingSecretValue
      : "••••••••"
    : "-";

  const viewingTypeMake = viewItem
    ? `${prettyType(viewItem.type)}${viewItem.make ? ` · ${viewItem.make}` : ""}`
    : "-";

  const viewingMailSubject = viewItem
    ? `Device: ${viewItem.nickname || "Details"}`
    : "Device details";

  const viewingSecretForShare = viewingSecretValue
    ? viewPwdRevealed
      ? viewingSecretValue
      : "(locked)"
    : "-";

  const viewingMailBody = viewItem
    ? [
        `Nickname: ${viewItem.nickname || "-"}`,
        `Type/Make: ${prettyType(viewItem.type)}${viewItem.make ? ` · ${viewItem.make}` : ""}`,
        `Number: ${viewItem.phoneNumber || "-"}`,
        `User: ${viewItem.username || "-"}`,
        `Email: ${viewItem.email || "-"}`,
        `Secret: ${viewingSecretForShare}`,
        `Notes: ${viewItem.notes || "-"}`,
      ].join("\n\n")
    : "";

  const viewingMailHref = `mailto:?subject=${encodeURIComponent(
    viewingMailSubject
  )}&body=${encodeURIComponent(viewingMailBody)}`;

  const actionsDesktop = (
    <>
      <button className="btn dp-help read" onClick={() => setShowDrawer(true)} data-allow-readonly>
        <FiEye className="dp-help-ico" />
        Read how it works
      </button>
      <button className="btn dp-help see" onClick={() => setShowVideo(true)} data-allow-readonly>
        <FaYoutube className="dp-help-ico" />
        See how it works
      </button>
    </>
  );
  const actionsMobile = (
    <div className="dp-help-row">
      <button className="btn dp-help read" onClick={() => setShowDrawer(true)} data-allow-readonly>
        <FiEye className="dp-help-ico" />
        Read how it works
      </button>
      <button className="btn dp-help see" onClick={() => setShowVideo(true)} data-allow-readonly>
        <FaYoutube className="dp-help-ico" />
        See how it works
      </button>
    </div>
  );

  return (
    <>
      <PageScaffold
        title="Devices"
        onAdd={!readOnly ? () => openForm() : undefined}
        heroSrc="/lastwish-account-Devices.jpg"
        heroAlt="Devices"
        actionsDesktop={actionsDesktop}
        actionsMobile={actionsMobile}
        
      >
        <div className="dv-list">
          {devices.length === 0 ? (
            <div className="empty-state">No devices yet.</div>
          ) : (
            devices.map((device) => <DeviceCard key={device.id} device={device} />)
          )}
        </div>
      </PageScaffold>

      {showForm && renderForm()}

      {/* View modal (read-only) */}
      {viewOpen && viewItem && (
        <div className="modal-bg" onClick={closeView}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 620 }}>
            <h3>Device</h3>
            <div className="modal-body">
              <div className="card" style={{ padding: 12, display: "grid", gap: 8 }}>
                {viewItem.imageUrl && (
                  <img
                    src={viewItem.imageUrl}
                    alt=""
                    style={{ width: "100%", maxHeight: 240, objectFit: "cover", borderRadius: 10 }}
                  />
                )}

                <div>
                  <b>Nickname:</b> {viewItem.nickname || "-"}
                </div>
                <div>
                  <b>Type/Make:</b> {viewingTypeMake}
                </div>
                {viewItem.phoneNumber && (
                  <div>
                    <b>Number:</b> {viewItem.phoneNumber}
                  </div>
                )}
                {viewItem.username && (
                  <div>
                    <b>User:</b> {viewItem.username}
                  </div>
                )}
                {viewItem.email && (
                  <div>
                    <b>Email:</b> {viewItem.email}
                  </div>
                )}

                <div>
                  <b>{viewingSecretLabel}:</b>{" "}
                  <span style={{ fontFamily: "monospace" }}>{viewingSecretDisplay}</span>
                  {viewingSecretValue && !viewPwdRevealed && (
                    <button
                      className="btn btn-main"
                      type="button"
                      onClick={handleRevealRequest}
                      data-allow-readonly
                      style={{ marginLeft: 8 }}
                    >
                      Unlock with your password
                    </button>
                  )}
                </div>

                {viewItem.notes && (
                  <div>
                    <b>Notes:</b> {viewItem.notes}
                  </div>
                )}
              </div>

              <div className="form-actions" style={{ marginTop: 10 }}>
                <button
                  className="btn btn-main"
                  type="button"
                  onClick={async () => {
                    const ownerUid = managedOwnerUid || user?.uid;

                    if (viewItem.imageName || viewItem.imageUrl) {
                      const base = viewItem.imageName
                        ? `devices/${ownerUid}/${viewItem.imageName}`
                        : toStoragePath(viewItem.imageUrl);
                      if (base && ownerUid) {
                        let url = await getSignedUrlFor(base);
                        if (!url) {
                          try {
                            url = await getDownloadURL(ref(storage, base));
                          } catch {
                            url = viewItem.imageUrl || null;
                          }
                        }
                        if (url) {
                          const link = document.createElement("a");
                          link.href = url;
                          link.download = (viewItem.nickname?.replace(/[^\w.-]+/g, "_") || "device") + ".jpg";
                          document.body.appendChild(link);
                          link.click();
                          link.remove();
                        }
                      }
                    }

                    const isPhone = (viewItem.type || "phone") === "phone";
                    const fields = [
                      ["Nickname", viewItem.nickname],
                      [
                        "Type/Make",
                        `${prettyType(viewItem.type)}${viewItem.make ? ` · ${viewItem.make}` : ""}`,
                      ],
                      ["Number", viewItem.phoneNumber],
                      ["User", viewItem.username],
                      ["Email", viewItem.email],
                      [
                        isPhone ? "Passcode" : "Password",
                        viewItem[isPhone ? "phonePasscode" : "loginPassword"]
                          ? viewPwdRevealed
                            ? viewItem[isPhone ? "phonePasscode" : "loginPassword"]
                            : "(locked)"
                          : "-",
                      ],
                      ["Notes", viewItem.notes],
                    ];
                    const txt = fields
                      .map(([k, v]) => `${k}: ${v ? String(v) : "-"}`)
                      .join("\n\n");
                    const fname = (viewItem.nickname?.replace(/[^\w.-]+/g, "_") || "device") + ".txt";
                    downloadTextFile(fname, txt);
                  }}
                  data-allow-readonly
                >
                  Download
                </button>
                <button
                  className="btn btn-main"
                  type="button"
                  onClick={() => window.print()}
                  data-allow-readonly
                >
                  Print
                </button>
                <a className="btn btn-main" href={viewingMailHref} data-allow-readonly>
                  Forward
                </a>
                <button className="btn btn-cancel" type="button" onClick={closeView} data-allow-readonly>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <VideoLightbox
        open={showVideo}
        onClose={() => setShowVideo(false)}
        url="https://youtu.be/RP1kSPoNYms?si=eFwob2MT5qgKJrSm"
        title="How it works (video)"
        startAt={0}
        captions={false}
        startMuted={false}
      />

      {showDrawer && (
        <div className="dp-drawer-bg" onClick={() => setShowDrawer(false)}>
          <aside className="dp-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="dp-drawer-head">
              <div className="dp-drawer-title">How Devices work</div>
              <div className="dp-drawer-tools">
                <button className="btn btn-main" onClick={() => window.print()} type="button" data-allow-readonly>
                  Print
                </button>
                <button className="btn btn-cancel" onClick={() => setShowDrawer(false)} type="button" data-allow-readonly>
                  Close
                </button>
              </div>
            </div>
            <div className="dp-drawer-body">
              <p>
                Catalog your phones and computers with nicknames, makes, and access info. Use the + button to add a device, and
                <b> Edit</b> to update details.
              </p>
              <ol>
                <li>Owners add/edit devices; managers see read-only.</li>
                <li>To view passcodes/passwords, managers confirm their own login password.</li>
                <li>Use Download to get the image and a text summary.</li>
              </ol>
            </div>
          </aside>
        </div>
      )}

      <style>{`
        .dv-list { display: grid; grid-template-columns: 1fr; gap: 16px; }
        .dv-item { display: grid; grid-template-columns: auto 1fr auto; align-items: center; gap: 12px; padding: 12px; }
        .dv-left { width: 42px; }
        .dv-main { min-width: 0; }
        .dv-main .tile-title { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .dv-actions { display: flex; gap: 8px; align-items: center; justify-self: end; }
        @media (max-width: 700px) { .dv-main .tile-title { font-size: 0.98rem; } .dv-main .tile-sub { font-size: 0.86rem; } }

        :root { --topbar-h: 56px; --orange:#f15822; --blue:#8cade1; }
        .dp-help-row { display:flex; gap:10px; flex-wrap:wrap; }
        .dp-help { border-radius: 8px; padding: 10px 14px; display: inline-flex; align-items: center; gap: 10px; font-weight: 700; color: #fff; border: none; }
        .dp-help.read { background: #650202; color: #fff; }
        .dp-help.see  { background: #003353; color: #fff; }
        .dp-help-ico { font-size: 1.05rem; }
        .dp-drawer-bg { position:fixed; inset:0; background:rgba(0,0,0,0.3); z-index:10000; display:flex; justify-content:flex-end; }
        .dp-drawer {
          position: fixed; top: var(--topbar-h, 56px); right: 0;
          height: calc(100vh - var(--topbar-h, 56px)); width: min(520px, 95vw);
          background: #fff; box-shadow: -8px 0 24px rgba(0,0,0,0.15);
          border-top-left-radius: 12px; border-bottom-left-radius: 12px;
          display: flex; flex-direction: column; overflow: hidden; animation: dp-slide-in .22s ease-out both;
        }
        @keyframes dp-slide-in { from { transform: translateX(20px); opacity:0; } to { transform: translateX(0); opacity:1; } }
        .dp-drawer-head { display:flex; align-items:center; justify-content:space-between; gap:10px; padding:14px 16px; border-bottom:1px solid #f0f0f0; background:#fafafa; }
        .dp-drawer-title { font-weight:700; color:#2a0516; }
        .dp-drawer-tools { display:flex; gap:8px; }
        .dp-drawer-body { padding:16px; overflow:auto; }
      `}</style>
      <UpgradeModal isOpen={showUpgrade} onClose={() => setShowUpgrade(false)} feature="documents" />
    </>
  );
}
